// src/components/CustomerOrderPage/CustomerLandingPage.jsx
// Guest entry WITHOUT a printed QR: type the event's short code (announced at
// the venue / shared on WhatsApp) → that event's sections & tables → menu.
// Also reachable as a deep link: /order/:eventCode.
// Replaces the old flow that listed EVERY public event's tables in one jumble.

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import api, { getGuestDisplayName, setGuestDisplayName } from '../../utils/api';
import BigButton from '../Shared/BigButton';
import LoadingOverlay from '../Shared/LoadingOverlay';
import PoweredByFooter from './PoweredByFooter';

const CODE_LEN = 6;

const CustomerLandingPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { eventCode: routeCode } = useParams();

	const [codeInput, setCodeInput] = useState('');
	const [checkingCode, setCheckingCode] = useState(false);
	const [event, setEvent] = useState(null); // { _id, name, branding } once the code resolves
	const [activeCode, setActiveCode] = useState('');
	const [tables, setTables] = useState([]);
	const [sections, setSections] = useState([]);
	const [selectedSection, setSelectedSection] = useState('');
	const [selectedTable, setSelectedTable] = useState('');
	const [guestName, setGuestName] = useState(() => getGuestDisplayName());
	const [loadingTables, setLoadingTables] = useState(false);
	const [error, setError] = useState(null);
	const [redirecting, setRedirecting] = useState(false);

	// QR scans land here with ?tableId= — pass straight through to the menu.
	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const tableId = params.get('tableId');
		if (tableId) {
			setRedirecting(true);
			const qrToken = params.get('token');
			const storedName = getGuestDisplayName();
			const nextParams = new URLSearchParams({ tableId });
			if (qrToken) nextParams.set('token', qrToken);
			if (storedName.trim()) nextParams.set('guestName', storedName.trim().slice(0, 60));
			navigate(`/customer/menu?${nextParams.toString()}`);
		}
	}, [location, navigate]);

	const resolveCode = useCallback(async (rawCode) => {
		const code = String(rawCode || '').toUpperCase().trim();
		if (code.length < 4) {
			setError('Event codes are 6 characters. Check the welcome sign or ask your host.');
			return;
		}
		setCheckingCode(true);
		setError(null);
		try {
			const res = await api.get(`/customer/event-by-code/${encodeURIComponent(code)}`);
			setEvent(res.data.event);
			setActiveCode(code);
			localStorage.setItem('lastEventCode', code);
		} catch (err) {
			setEvent(null);
			setError(err.response?.data?.msg || 'Could not find that event. Check the code and try again.');
		} finally {
			setCheckingCode(false);
		}
	}, []);

	// Deep link /order/:eventCode resolves immediately.
	useEffect(() => {
		if (routeCode) {
			setCodeInput(routeCode.toUpperCase());
			resolveCode(routeCode);
		} else {
			const last = localStorage.getItem('lastEventCode');
			if (last) setCodeInput(last);
		}
	}, [routeCode, resolveCode]);

	// Once the event resolves, load ITS tables only.
	useEffect(() => {
		if (!activeCode || !event) return;
		let alive = true;
		(async () => {
			setLoadingTables(true);
			try {
				// Retry transient failures (sleeping dyno cold-start).
				let res;
				for (let attempt = 1; ; attempt += 1) {
					try {
						res = await api.get('/customer/tables', { params: { code: activeCode } });
						break;
					} catch (err) {
						const status = err.response?.status;
						if ((!status || status >= 500) && attempt < 3) {
							await new Promise((r) => setTimeout(r, 1500 * attempt));
							continue;
						}
						throw err;
					}
				}
				if (!alive) return;
				const allTables = res.data || [];
				setTables(allTables);
				const sectionMap = new Map();
				let uncategorized = 0;
				allTables.forEach((t) => {
					if (t.category?._id) sectionMap.set(t.category._id, t.category.name || 'Section');
					else uncategorized += 1;
				});
				const secs = [...sectionMap.entries()].map(([id, name]) => ({ _id: id, name }));
				if (uncategorized > 0) secs.push({ _id: '__uncategorized__', name: 'General seating' });
				setSections(secs);
				// One section (or none)? Skip the section step entirely.
				if (secs.length === 1) setSelectedSection(secs[0]._id);
			} catch (err) {
				if (alive) setError(err.response?.data?.msg || 'Unable to load tables. Please try again.');
			} finally {
				if (alive) setLoadingTables(false);
			}
		})();
		return () => { alive = false; };
	}, [activeCode, event]);

	const visibleTables = tables
		.filter((t) => {
			if (!selectedSection) return false;
			if (selectedSection === '__uncategorized__') return !t.category;
			return t.category && t.category._id === selectedSection;
		})
		.sort((a, b) => String(a.tableNumber).localeCompare(String(b.tableNumber), undefined, { numeric: true }));

	const goToMenu = (e) => {
		e.preventDefault();
		if (!selectedTable) return;
		const params = new URLSearchParams({ tableId: selectedTable, ecode: activeCode });
		const clean = guestName.trim().slice(0, 60);
		if (clean) {
			setGuestDisplayName(clean);
			params.set('guestName', clean);
		}
		navigate(`/customer/menu?${params.toString()}`);
	};

	if (redirecting) {
		return <LoadingOverlay show={true} message="Opening the menu..." fullScreen />;
	}

	const brandColor = event?.branding?.primaryColor || '#3D1E6D';

	return (
		<div className="ev-customer-page" style={{ '--ev-brand-color': brandColor }}>
			<div className="ev-customer-main">
				<div className="ev-customer-card">
					<img src="/images/eventory-logo.svg" alt="Eventory" className="ev-customer-logo" />

					{!event ? (
						<>
							<p className="ev-customer-kicker">You're invited</p>
							<h1 className="ev-customer-title">What's your event code?</h1>
							<p className="ev-customer-subtitle">
								It's on the welcome sign or your table card, or ask your host. 6 letters and numbers.
							</p>
							{error && <div className="ev-alert ev-alert-danger">{error}</div>}
							<form
								onSubmit={(e) => {
									e.preventDefault();
									resolveCode(codeInput);
								}}
							>
								<input
									type="text"
									className="ev-customer-input ev-customer-code-input"
									value={codeInput}
									onChange={(e) => setCodeInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, CODE_LEN))}
									placeholder="K7M2PX"
									autoComplete="off"
									autoCapitalize="characters"
									spellCheck="false"
									inputMode="text"
									aria-label="Event code"
								/>
								<BigButton type="submit" variant="brand" size="xlarge" fullWidth disabled={checkingCode || codeInput.length < 4}>
									{checkingCode ? 'Finding your event…' : 'Find my event →'}
								</BigButton>
							</form>
							<div className="ev-customer-divider">
								<p className="ev-customer-helper">Have a table access code instead?</p>
								<BigButton variant="brandOutline" size="medium" onClick={() => navigate('/order/code')}>
									Enter table code
								</BigButton>
							</div>
						</>
					) : (
						<>
							<h1 className="ev-customer-title">{event.name}</h1>
							<p className="ev-customer-subtitle">
								{event.branding?.welcomeMessage || 'Pick your seat to see the menu.'}
							</p>
							{error && <div className="ev-alert ev-alert-danger">{error}</div>}
							{loadingTables ? (
								<p className="ev-subtle">Loading tables…</p>
							) : (
								<form onSubmit={goToMenu}>
									{sections.length > 1 && (
										<>
											<label className="ev-customer-label">
												<span className="ev-customer-step">1</span> Your section
											</label>
											<div className="ev-customer-section-grid">
												{sections.map((section) => (
													<button
														key={section._id}
														type="button"
														className={`ev-customer-section-btn ${selectedSection === section._id ? 'is-active' : ''}`}
														onClick={() => {
															setSelectedSection(section._id);
															setSelectedTable('');
														}}
													>
														{section.name}
													</button>
												))}
											</div>
										</>
									)}

									<label className="ev-customer-label ev-top-gap">
										<span className="ev-customer-step">{sections.length > 1 ? 2 : 1}</span> Your table
									</label>
									{!selectedSection ? (
										<p className="ev-subtle ev-bottom-gap">Pick a section first.</p>
									) : visibleTables.length === 0 ? (
										<p className="ev-subtle ev-bottom-gap">No tables in this section yet.</p>
									) : (
										<div className="ev-customer-table-grid">
											{visibleTables.map((table) => (
												<button
													key={table._id}
													type="button"
													className={`ev-customer-table-btn ${selectedTable === table._id ? 'is-active' : ''}`}
													onClick={() => setSelectedTable(table._id)}
												>
													<span className="ev-customer-table-label">Table</span>
													<span className="ev-customer-table-number">{table.tableNumber}</span>
												</button>
											))}
										</div>
									)}

									<label className="ev-customer-label ev-top-gap">
										<span className="ev-customer-step">{sections.length > 1 ? 3 : 2}</span> Your name{' '}
										<span className="ev-customer-optional">(optional)</span>
									</label>
									<input
										type="text"
										className="ev-customer-input"
										value={guestName}
										onChange={(e) => setGuestName(e.target.value)}
										placeholder="So the kitchen can call your order by name"
										maxLength={60}
									/>

									<BigButton type="submit" variant="brand" size="xlarge" fullWidth disabled={!selectedTable}>
										View the menu →
									</BigButton>
								</form>
							)}
							<div className="ev-customer-divider">
								<button
									type="button"
									className="ev-inline-link"
									onClick={() => {
										setEvent(null);
										setActiveCode('');
										setSections([]);
										setTables([]);
										setSelectedSection('');
										setSelectedTable('');
										setError(null);
									}}
								>
									← Different event? Change code
								</button>
							</div>
						</>
					)}
				</div>
			</div>
			<PoweredByFooter tone="dark" />
		</div>
	);
};

export default CustomerLandingPage;
